import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import FEATURES, { FEATURE_BY_ROUTE } from '../data/features';
import ApertureMark from './ApertureMark';

const GITHUB_URL = 'https://github.com/Kenny-Rogers/vantage-labs';

// The persistent workspace shell: top bar + sidebar + status bar wrap every
// route. Only the main pane (<Outlet/>) changes as you switch features.
function WorkspaceLayout() {
  const { pathname } = useLocation();
  const isAbout = pathname === '/about';
  const activeFeature = FEATURE_BY_ROUTE[pathname];
  // null = "auto": let CSS decide the default per breakpoint (sidebar open on
  // desktop, drawer closed on mobile) so the shared prerendered HTML paints
  // correctly on every viewport. Becomes an explicit boolean once toggled.
  const [collapsed, setCollapsed] = useState(null);
  const isMobile = () =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 840px)').matches;

  const toggleTools = () =>
    setCollapsed((prev) => (prev !== null ? !prev : !isMobile()));

  // On mobile, picking a tool should close the drawer.
  const closeOnMobile = () => {
    if (isMobile()) setCollapsed(true);
  };

  const collapsedAttr = collapsed === null ? 'auto' : collapsed ? 'true' : 'false';

  // ⌘/ (or Ctrl+/) jumps focus to the active sidebar entry — a quick way to
  // arrow between tools from the keyboard.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        document.querySelector('.ws-feat[data-active="1"]')?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  let statusTool = 'view: about';
  let statusState = 'ok';
  if (!isAbout && activeFeature) {
    statusTool = `tool: ${activeFeature.name.toLowerCase()}`;
    statusState = activeFeature.live ? 'ok' : 'in development';
  }

  return (
    <div className="ws" data-collapsed={collapsedAttr}>
      {/* ── Top bar ───────────────────────────────── */}
      <div className="ws-topbar">
        <Link className="ws-brand" to="/">
          <ApertureMark className="ws-logo" />
          <span className="ws-wordmark">
            Vantage<span className="ws-brand-accent">Labs</span>
          </span>
        </Link>
        <span className="ws-divider" />
        <div className="ws-top-tabs">
          <Link to="/" className="ws-tab" data-active={!isAbout ? '1' : '0'}>
            Workspace
          </Link>
          <Link to="/about" className="ws-tab" data-active={isAbout ? '1' : '0'}>
            About
          </Link>
        </div>
        <span className="ws-spacer" />
        <div className="ws-top-meta">
          <span className="mono ws-locally">
            <span className="ws-pip" aria-hidden="true" />
            runs locally
          </span>
          <span className="ws-divider" />
          <span className="ws-kbd">⌘ /</span>
        </div>
      </div>

      {/* ── Sidebar ───────────────────────────────── */}
      <aside className="ws-sidebar">
        <div className="ws-side-section ws-side-head">
          <h4>Tools</h4>
          <button
            type="button"
            className="ws-side-collapse"
            aria-label={collapsed ? 'Show tools' : 'Hide tools'}
            aria-expanded={collapsed !== true}
            onClick={toggleTools}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d={collapsed ? 'M6 3l5 5-5 5' : 'M10 3 5 8l5 5'}
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="ws-side-body">
          <ul className="ws-side-list">
            {FEATURES.map((f) => (
              <li key={f.id}>
                <Link
                  to={f.route}
                  className="ws-feat"
                  data-active={!isAbout && pathname === f.route ? '1' : '0'}
                  data-live={f.live ? '1' : '0'}
                  data-soon={f.live ? '0' : '1'}
                  onClick={closeOnMobile}
                >
                  <span className="ws-dot" />
                  <span className="ws-feat-text">
                    <span className="ws-feat-name">{f.name}</span>
                    <span className="ws-feat-sub">{f.kind}</span>
                  </span>
                  <span className="ws-badge">{f.live ? 'live' : 'soon'}</span>
                </Link>
              </li>
            ))}
          </ul>

          <div className="ws-side-section">
            <h4>Workspace</h4>
          </div>
          <ul className="ws-side-list">
            <li>
              <Link
                to="/about"
                className="ws-feat"
                data-active={isAbout ? '1' : '0'}
                onClick={closeOnMobile}
              >
                <span className="ws-dot ws-dot-muted" />
                <span className="ws-feat-text">
                  <span className="ws-feat-name">About</span>
                  <span className="ws-feat-sub">Notes from the lab</span>
                </span>
                <span />
              </Link>
            </li>
          </ul>
        </div>
      </aside>

      {/* Mobile drawer backdrop — tap outside to close. */}
      <button
        type="button"
        className="ws-side-backdrop"
        aria-hidden="true"
        tabIndex={-1}
        onClick={() => setCollapsed(true)}
      />

      {/* ── Main pane ─────────────────────────────── */}
      <main className="ws-main">
        <Outlet />
      </main>

      {/* ── Status bar ────────────────────────────── */}
      <div className="ws-statbar">
        <div className="ws-statbar-group">
          <span>
            <span className="ws-statbar-pip" />
            ready
          </span>
          <span className="ws-sep ws-statbar-meta">·</span>
          <span className="ws-statbar-meta">{statusTool}</span>
          <span className="ws-sep ws-statbar-meta">·</span>
          <span className="ws-statbar-meta">{statusState}</span>
        </div>
        <div className="ws-statbar-group">
          <span className="ws-statbar-meta">render · canvas2d</span>
          <span className="ws-sep ws-statbar-meta">·</span>
          <span className="ws-statbar-meta">
            {new Date().toLocaleDateString('en-CA')}
          </span>
          <span className="ws-sep ws-statbar-meta">·</span>
          <a
            className="ws-statbar-link"
            href={GITHUB_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            GitHub ↗
          </a>
        </div>
      </div>
    </div>
  );
}

export default WorkspaceLayout;
